import {useFormik} from 'formik';
import * as Yup from 'yup';
import {useState} from 'react';
import {AxiosError} from 'axios';
import {useFundraiser} from '../../../entities';
import {ProjectTransferFormData, ProjectTransferDTO} from '../../../entities';
import {normalizeTransferAmount, parseTransferAmount} from '../lib/transferAmount';

interface UseProjectTransferFormProps {
  fundraiserId: string;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
}

export const useProjectTransferForm = ({fundraiserId, setOpen, onSuccess}: UseProjectTransferFormProps) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formDataForConfirm, setFormDataForConfirm] = useState<ProjectTransferDTO | null>(null);
  const {transferFromProjectMutation} = useFundraiser({id: fundraiserId});

  const validationSchema = Yup.object({
    amount: Yup.string()
      .required('Сумма обязательна')
      .test('amount-format', 'Должно быть числом', (value, context) => {
        if (!value || parseTransferAmount(value) !== null) return true;
        return normalizeTransferAmount(value) === value
          ? context.createError({message: 'Сумма слишком велика для точной передачи копеек'})
          : false;
      })
      .test('amount-positive', 'Сумма должна быть положительной', (value) => {
        const amount = value ? parseTransferAmount(value) : null;
        return amount === null || amount > 0;
      }),
  });

  const onSubmit = async (values: ProjectTransferFormData) => {
    const amount = parseTransferAmount(values.amount);
    if (amount === null || amount <= 0) return;

    const transferData: ProjectTransferDTO = {
      amount,
    };

    setFormDataForConfirm(transferData);
    setShowConfirmModal(true);
  };

  const handleConfirmTransfer = async () => {
    if (!formDataForConfirm) return;

    try {
      await transferFromProjectMutation.mutateAsync(formDataForConfirm);
      setShowConfirmModal(false);
      setOpen(false);
      formik.resetForm();
      onSuccess?.();
    } catch (error) {
      console.error('Ошибка при внесении средств:', error);
    }
  };

  const formik = useFormik<ProjectTransferFormData>({
    initialValues: {
      amount: '',
    },
    validationSchema,
    onSubmit,
  });

  return {
    ...formik,
    setFieldTouched: formik.setFieldTouched,
    isLoading: transferFromProjectMutation.isPending,
    submitError: transferFromProjectMutation.error
      ? (transferFromProjectMutation.error as AxiosError).response?.data
      : undefined,
    showConfirmModal,
    setShowConfirmModal,
    formDataForConfirm,
    handleConfirmTransfer,
    submitForm: () => formik.handleSubmit(),
  };
};
